import React, { useState, useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Award } from "lucide-react";

import type { FormikProps } from "formik";
import { useTranslation } from "react-i18next";

import type { SilaeFormData } from "@/types/silae";
import { createEmptyBonus } from "@/hooks/useSilaeWizardFormik";
import BonusConfig from "../BonusConfig";

interface Step3ConfigurationIFProps {
  formik: FormikProps<SilaeFormData>;
}

/**
 * Étape 3 : Configuration de la branche IF avec support multi-primes via tabs
 */
export const Step3ConfigurationIF: React.FC<Step3ConfigurationIFProps> = ({
  formik,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("bonus-0");

  const bonuses = useMemo(
    () => formik.values.ifBranch.bonuses,
    [formik.values.ifBranch.bonuses],
  );

  const addBonus = useCallback(() => {
    const newBonus = createEmptyBonus();

    // Si c'est la deuxième prime ou plus, activer automatiquement les conditions
    if (bonuses.length >= 1) {
      newBonus.hasConditions = true;
      // Ajouter une condition par défaut
      newBonus.conditions = [
        {
          id: `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: "VAR_SALARY_BASE",
          silaeVariable: "bul.salairedebase",
          conditionOperator: ">=",
          conditionValue: "",
        },
      ];

      // Activer aussi les conditions pour toutes les primes existantes
      const updatedBonuses = bonuses.map((bonus) => {
        if (!bonus.hasConditions) {
          return {
            ...bonus,
            hasConditions: true,
            conditions:
              bonus.conditions.length === 0
                ? [
                    {
                      id: `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                      type: "VAR_SALARY_BASE",
                      silaeVariable: "bul.salairedebase",
                      conditionOperator: ">=",
                      conditionValue: "",
                    },
                  ]
                : bonus.conditions,
          };
        }
        return bonus;
      });

      updatedBonuses.push(newBonus);
      formik.setFieldValue("ifBranch.bonuses", updatedBonuses);
    } else {
      const updatedBonuses = [...bonuses, newBonus];
      formik.setFieldValue("ifBranch.bonuses", updatedBonuses);
    }

    setActiveTab(`bonus-${bonuses.length}`);
  }, [bonuses, formik]);

  const removeBonus = useCallback(
    (index: number) => {
      if (bonuses.length <= 1) return;
      const updatedBonuses = bonuses.filter((_, i) => i !== index);
      formik.setFieldValue("ifBranch.bonuses", updatedBonuses);
      if (activeTab === `bonus-${index}`) {
        setActiveTab("bonus-0");
      }
    },
    [bonuses, formik, activeTab],
  );

  return (
    <Card className="space-y-6 p-4 gap-4 rounded-3xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">
            {t("step3Configuration.bonusesManagement")}
          </h3>
        </div>
        <Button
          type="button"
          onClick={addBonus}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t("step3Configuration.addBonus")}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto">
          {bonuses.map((bonus, index) => (
            <div key={index} className="flex items-center gap-1">
              <TabsTrigger
                value={`bonus-${index}`}
                className="flex items-center gap-2"
              >
                <Award className="h-3 w-3" />
                {bonus.libelle ||
                  `${t("step3Configuration.bonus")} ${index + 1}`}
                {bonuses.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBonus(index);
                    }}
                    className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </TabsTrigger>
            </div>
          ))}
        </TabsList>

        {bonuses.map((_, index) => (
          <TabsContent key={index} value={`bonus-${index}`}>
            <BonusConfig
              formik={formik}
              bonusIndex={index}
              totalBonuses={bonuses.length}
            />
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
};
